import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'
import DesktopDataTable from './DesktopDataTable'
import MobileDataTable from './MobileDataTable'

/**
 * Renders rows as a desktop table or mobile cards. The first column is
 * emphasized on desktop and used as the card title on mobile.
 */
const DataTable = ({
  columnGap = '24px',
  trailingWidth = '44px',
  breakpoint = MOBILE_VIEWPORT,
  ...props
}) => {
  const isMobile = useMediaQuery(breakpoint)

  return isMobile ? (
    <MobileDataTable {...props} />
  ) : (
    <DesktopDataTable columnGap={columnGap} trailingWidth={trailingWidth} {...props} />
  )
}

export const columnPropTypes = {
  /** Key into each row's `values` map. */
  key: PropTypes.string.isRequired,
  /** Desktop heading and mobile field label. */
  label: PropTypes.string,
  /** Desktop cell alignment; defaults to left. */
  align: PropTypes.oneOf(['left', 'right']),
  /** Desktop width in CSS grid `fr` units; the first column defaults to 2, the rest to 1. */
  fr: PropTypes.number,
}

DataTable.propTypes = {
  /** Columns in display order. */
  columns: PropTypes.arrayOf(PropTypes.shape(columnPropTypes)).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      /** DOM `id` for the rendered row or card; lets callers target a specific item. */
      domId: PropTypes.string,
      /** Applies muted styling without disabling interaction. */
      muted: PropTypes.bool,
      /** Cell content keyed by column key. */
      values: PropTypes.objectOf(PropTypes.node).isRequired,
      /** Plain-text override for the mobile card title. */
      cardTitle: PropTypes.string,
      /** Content shown in the desktop trailing cell or mobile card header. */
      trailing: PropTypes.node,
    })
  ).isRequired,
  className: PropTypes.string,
  /** Media query that selects the card layout; defaults to `MOBILE_VIEWPORT`. */
  breakpoint: PropTypes.string,
  /** Gap between desktop columns. */
  columnGap: PropTypes.string,
  /** Width of the desktop trailing column; added when any row has trailing content. */
  trailingWidth: PropTypes.string,
}

export default DataTable
